import type { ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

/**
 * The console's one detail surface.
 *
 * A drawer rather than a page because everything it shows is about a row on
 * the list behind it: closing it should put the stylist back exactly where she
 * was, mid-scroll, with her filters intact. Radix carries the parts that are
 * easy to get wrong and impossible to see when they are — focus trapping,
 * `aria-modal`, restoring focus to the row that opened it, and Escape.
 *
 * `onPrevious`/`onNext` are optional: they exist so a stylist working a day
 * can walk it without closing and reopening, and they are simply absent when
 * there is nothing to walk.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  eyebrow,
  onPrevious,
  onNext,
  footer,
  children,
}: {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly title: string
  readonly eyebrow?: string
  readonly onPrevious?: (() => void) | undefined
  readonly onNext?: (() => void) | undefined
  readonly footer?: ReactNode
  readonly children: ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="st-overlay print-hide" />
        <Dialog.Content
          className="st-drawer st-root print-hide"
          aria-describedby={undefined}
          /*
           * Radix focuses the first focusable child, which here is the
           * "Previous" arrow — so a stylist arriving by keyboard is told she
           * is on a button before she is told whose appointment she opened.
           * Focusing the panel puts its own title first, and Tab still lands
           * on the arrows next.
           */
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            const panel = event.currentTarget
            if (panel instanceof HTMLElement) panel.focus()
          }}
        >
          <header className="flex items-start gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--st-rule)' }}>
            <div className="min-w-0 flex-1">
              {eyebrow ? <div className="st-eyebrow mb-1">{eyebrow}</div> : null}
              <Dialog.Title className="st-display truncate text-xl">{title}</Dialog.Title>
            </div>
            {onPrevious || onNext ? (
              <div className="flex gap-1">
                <button type="button" className="st-iconbtn" onClick={onPrevious} disabled={!onPrevious} aria-label="Previous">
                  <ChevronLeft size={16} aria-hidden />
                </button>
                <button type="button" className="st-iconbtn" onClick={onNext} disabled={!onNext} aria-label="Next">
                  <ChevronRight size={16} aria-hidden />
                </button>
              </div>
            ) : null}
            <Dialog.Close asChild>
              <button type="button" className="st-iconbtn" aria-label="Close">
                <X size={16} aria-hidden />
              </button>
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer ? (
            <footer className="border-t px-5 py-3" style={{ borderColor: 'var(--st-rule)' }}>
              {footer}
            </footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
